import React, { useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import employeeService from "../../api/services/employeeService";
import SafeImage from "../../components/common/SafeImage";

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef();

  // Fetch employee details using TanStack Query
  const {
    data: employee,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["employee", id],
    queryFn: () => employeeService.getEmployeeById(id),
    select: (response) => (response.success ? response.data : null),
  });

  // Profile image upload mutation
  const { mutate: updateProfileImage, isPending: uploading } = useMutation({
    mutationFn: (file) =>
      employeeService.updateProfileImage(employee.employee_id, file),
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Profile image updated!");
        // Invalidate the employee query to refetch updated data
        queryClient.invalidateQueries(["employee", id]);
      } else {
        toast.error(result.message || "Failed to update profile image");
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update profile image");
    },
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusClass = status === "active" ? "bg-success" : "bg-danger";
    return (
      <span className={`badge ${statusClass} text-capitalize`}>{status}</span>
    );
  };

  // Handle profile image upload
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    updateProfileImage(file);
  };

  // Helper to get full image URL
  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return "/avatar-placeholder.png";
    if (profileImage.startsWith("/storage/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") + profileImage
      );
    }
    return profileImage;
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div
          className="spinner-border text-primary mb-3"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted">Loading employee details...</p>
      </div>
    );
  }

  if (isError || !employee) {
    const errorMessage =
      error?.message || "The requested employee could not be found.";
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div className="alert alert-danger p-4 rounded-4 shadow-sm text-center">
          <h4 className="mb-2">
            <i className="ri-error-warning-line me-2"></i>Employee Not Found
          </h4>
          <p>{errorMessage}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/employee")}
          >
            <i className="ri-arrow-left-line me-2"></i>Back to Employee List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center border-bottom pb-3 mb-3 gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-user-3-line text-primary fs-2"></i>
            <h2 className="fw-bold mb-0">Employee Details</h2>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/employee">Employees</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {employee.user?.name || "Employee Details"}
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="row g-4">
        {/* Profile Card */}
        <div className="col-12 col-lg-4">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body d-flex flex-column align-items-center text-center">
              <div
                className="position-relative mb-3"
                style={{ width: 140, height: 140 }}
              >
                <SafeImage
                  src={getProfileImageUrl(employee.user?.profile_image)}
                  className="img-fluid rounded-circle border border-3 border-white shadow"
                  alt={employee.user?.name}
                  style={{ width: 140, height: 140, objectFit: "cover" }}
                  fallbackSrc="/avatar-placeholder.png"
                />
                {/* Pencil Icon Button for Profile Image Edit */}
                <button
                  type="button"
                  className="btn-light position-absolute d-flex align-items-center justify-content-center border shadow p-0"
                  style={{
                    bottom: 6,
                    right: 6,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    zIndex: 2,
                    lineHeight: 1,
                  }}
                  onClick={() =>
                    !uploading &&
                    fileInputRef.current &&
                    fileInputRef.current.click()
                  }
                  title={uploading ? "Uploading..." : "Edit Profile Image"}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  disabled={uploading}
                >
                  {uploading ? (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="ri-pencil-line fs-5 text-secondary"></i>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  disabled={uploading}
                />
              </div>
              <h3 className="fw-semibold mb-1">{employee.user?.name}</h3>
              <div className="text-muted mb-2">
                {employee.user?.role || "Employee"}
              </div>
              <div className="mb-2 small text-muted">
                <span className="me-2">
                  <i className="ri-hashtag"></i> {employee.employee_id}
                </span>
                <span>
                  <i className="ri-calendar-line"></i> Joined{" "}
                  {formatDate(employee.created_at)}
                </span>
              </div>
              <div className="d-flex gap-2 mt-2">
                <Link
                  to={`/employeeaddorupdate/${employee.employee_id}`}
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="ri-pencil-line me-1"></i>Edit
                </Link>
                <Link
                  to="/employee"
                  className="btn btn-outline-secondary btn-sm"
                >
                  <i className="ri-arrow-left-line me-1"></i>Back
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="col-12 col-lg-8">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body">
              <div className="row g-4">
                {/* Personal Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Full Name</div>
                    <div className="fw-semibold">
                      {employee.user?.name || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Email</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a
                        href={`mailto:${employee.user?.email}`}
                        className="text-decoration-none"
                      >
                        {employee.user?.email || "N/A"}
                      </a>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Email"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(employee.user?.email);
                          toast.success("Email copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Phone</div>
                    <div className="fw-semibold">
                      <a
                        href={`tel:${employee.user?.phone_no}`}
                        className="text-decoration-none"
                      >
                        {employee.user?.phone_no || "N/A"}
                      </a>
                    </div>
                  </div>
                </div>
                {/* Account Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">User ID</div>
                    <div className="fw-semibold">{employee.user?.user_id}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Status</div>
                    <div className="fw-semibold">
                      {getStatusBadge(employee.user?.status)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Last Updated</div>
                    <div className="fw-semibold">
                      {formatDate(employee.updated_at)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row g-4 mt-2">
                {/* Quick Actions */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Employee ID</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <span>#{employee.employee_id}</span>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Employee ID"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(employee.employee_id);
                          toast.success("Employee ID copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Created</div>
                    <div className="fw-semibold">
                      {formatDate(employee.user?.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
